import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import NotFound from './404';

const productData = {
  'path-view': {
    name: 'Path View',
    category: 'Education operations',
    headline: 'One connected view of the learning operation.',
    description: 'Path View is designed to bring learning delivery, administration, communication, and institutional oversight into one coherent environment.',
    audience: ['Schools and colleges', 'Training academies', 'Institution groups', 'Education teams'],
    features: [
      ['Learning delivery', 'Organize course content, learning activity, and the day-to-day student experience.'],
      ['Institution workflows', 'Support the administrative processes that sit around teaching and learning.'],
      ['Role-aware access', 'Give students, educators, and administrators a view shaped around their responsibilities.'],
      ['Operational visibility', 'Bring important activity into a clearer shared view for institutional teams.'],
      ['Communication', 'Create a consistent place for relevant updates and collaboration.'],
      ['Adaptable structure', 'Configure the experience around the way an institution is organized.'],
    ],
    portal: 'https://nalexus.online',
    galleryNote: 'These privacy-safe crops come from the active Path View environment. Every view stops before any student, faculty, campus identity, or private record appears.',
    gallery: [
      {
        image: '/assets/img/path-view-campus-actions.jpg',
        title: 'Campus operations at a glance',
        text: 'A privacy-safe crop of the live campus workspace, showing role-aware access to students, faculty, attendance, reports, and payment workflows.',
      },
      {
        image: '/assets/img/path-view-student-tools.jpg',
        title: 'Student administration tools',
        text: 'The live student module includes approvals, login generation, class-list exports, and student-card workflows.',
      },
      {
        image: '/assets/img/path-view-faculty-tools.jpg',
        title: 'Faculty workspace',
        text: 'A searchable faculty workspace designed for quick access without exposing any teacher identity or record in this public preview.',
      },
    ],
  },
  restomatic: {
    name: 'Restomatic',
    category: 'Restaurant operations',
    headline: 'A restaurant ecosystem designed to work as one.',
    description: 'Restomatic is designed to connect the customer journey with front-of-house, kitchen, and management workflows in one operational platform.',
    audience: ['Independent restaurants', 'Restaurant groups', 'Hospitality operators', 'Food-service teams'],
    features: [
      ['Ordering experience', 'Support clear digital ordering journeys across the touchpoints a restaurant chooses to offer.'],
      ['Point of service', 'Keep staff workflows focused and connected to the rest of the operation.'],
      ['Kitchen coordination', 'Move orders and status information through a kitchen-focused workflow.'],
      ['Reservations and tables', 'Bring guest planning and table activity into the same operating environment.'],
      ['Management view', 'Make day-to-day operational information easier for managers to see and act on.'],
      ['Brand experience', 'Present a restaurant experience that can reflect the operator’s own brand.'],
    ],
    galleryNote: 'These screens come from Restomatic’s public demonstration portals and show no customer, staff, reservation, payment, or order records.',
    gallery: [
      {
        image: '/assets/img/restomatic-portal-selector.jpg',
        title: 'One product, multiple operating roles',
        text: 'Customer, administration, cashier, waiter, multi-branch, and kitchen experiences are organized from one portal selector.',
      },
      {
        image: '/assets/img/restomatic-pos.jpg',
        title: 'Point-of-sale workspace',
        text: 'A live demonstration view of product selection, payment and discount controls, order totals, and restaurant integrations.',
      },
      {
        image: '/assets/img/restomatic-customer-portal.jpg',
        title: 'Digital menu experience',
        text: 'A branded menu journey with categories, product cards, pricing, and image-assisted discovery.',
      },
    ],
  },
};

export default function ProductDetail() {
  const { productId } = useParams();
  const location = useLocation();
  const product = productData[productId];
  const demoHref = useMemo(() => {
    const source = new URLSearchParams(location.search);
    const target = new URLSearchParams({ intent: 'demo', product: productId });
    ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => {
      if (source.get(key)) target.set(key, source.get(key));
    });
    return `/contact?${target.toString()}`;
  }, [location.search, productId]);

  useEffect(() => {
    if (product) document.title = `${product.name} | Nalexus Technologies`;
  }, [product]);

  if (!product) return <NotFound />;

  return (
    <main id="main-content" className="sync-main sync-subpage">
      <section className="sync-page-hero sync-product-detail-hero sync-section">
        <div className="sync-shell sync-page-hero-grid">
          <div>
            <p className="sync-eyebrow">{product.category}</p>
            <h1>{product.headline}</h1>
            <p className="sync-lead">{product.description}</p>
            <div className="sync-actions">
              <Link className="sync-button" to={demoHref}>Request a product demo</Link>
              {product.portal ? (
                <a className="sync-text-link" href={product.portal} target="_blank" rel="noreferrer">Open customer portal <span aria-hidden="true">↗</span></a>
              ) : (
                <Link className="sync-text-link" to="/contact">Discuss implementation <span aria-hidden="true">→</span></Link>
              )}
            </div>
            {product.portal && <p className="sync-portal-note">Customer login is separate from a product demo.</p>}
          </div>
          <div className="sync-product-detail-mark" aria-hidden="true">
            <span>{product.name.split(' ').map((word) => word[0]).join('')}</span>
            <i />
          </div>
        </div>
      </section>

      <section className="sync-section" aria-labelledby="capabilities-heading">
        <div className="sync-shell">
          <div className="sync-section-heading sync-heading-row">
            <div>
              <p className="sync-kicker">Capabilities</p>
              <h2 id="capabilities-heading">A connected foundation, shaped around the operation.</h2>
            </div>
            <p>Specific modules and integrations should be confirmed during discovery so the delivered product matches the organization’s actual workflows.</p>
          </div>
          <div className="sync-solution-grid">
            {product.features.map(([title, text], index) => (
              <article key={title} className="sync-solution-item">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sync-section sync-product-gallery-section" aria-labelledby="product-gallery-heading">
        <div className="sync-shell">
          <div className="sync-section-heading sync-heading-row">
            <div>
              <p className="sync-kicker">Live product views</p>
              <h2 id="product-gallery-heading">A closer look at the working experience.</h2>
            </div>
            <p>{product.galleryNote}</p>
          </div>
          <div className="sync-product-gallery-grid">
            {product.gallery.map((item) => (
              <figure className="sync-product-gallery-card" key={item.image}>
                <div className="sync-product-gallery-image">
                  <img src={item.image} alt={`${product.name}: ${item.title}`} loading="lazy" />
                </div>
                <figcaption>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="sync-section sync-audience-section" aria-labelledby="audience-heading">
        <div className="sync-shell sync-audience-grid">
          <div>
            <p className="sync-kicker">Designed for</p>
            <h2 id="audience-heading">Teams that need one dependable operating view.</h2>
          </div>
          <ul>{product.audience.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </section>

      <section className="sync-section sync-final-cta">
        <div className="sync-shell sync-final-cta-inner">
          <p className="sync-kicker">See the workflow, then decide.</p>
          <h2>Request a guided {product.name} demonstration for your team.</h2>
          <Link className="sync-button" to={demoHref}>Request demo</Link>
        </div>
      </section>
    </main>
  );
}
